import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FaCheck } from "react-icons/fa";
import { bulkApprovePaymentRequests } from "redux/erp/paymentRequestSlice";
import { Modal, Button, FormInput } from "components/common";

/**
 * Xác nhận ký hàng loạt.
 *
 * BE luôn trả 200 kèm { approved, failed } — lỗi từng phiếu (hay gặp nhất là
 * chưa upload chữ ký ở HR Hub) không làm hỏng cả lô. Nên sau khi gọi xong phải
 * hiện lại danh sách phiếu lỗi kèm lý do, thay vì chỉ toast "thành công".
 */
const BulkApproveForm = ({ show, onHide, ids = [], onDone }) => {
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (show) { setComment(""); setResult(null); setBusy(false); }
  }, [show]);

  const onSubmit = async () => {
    setBusy(true);
    try {
      const res = await dispatch(bulkApprovePaymentRequests({ ids, comment })).unwrap();
      const failed = res?.failed || [];
      const approved = res?.approved || [];

      if (failed.length === 0) {
        toast.success(`Approved ${approved.length} payment request(s).`);
        onDone?.();
        onHide();
        return;
      }

      // Còn phiếu lỗi -> giữ modal lại để người ký đọc được lý do từng phiếu.
      setResult(res);
      if (approved.length) toast.warning(`Approved ${approved.length}, ${failed.length} failed.`);
      else toast.error("No payment request could be approved.");
      onDone?.();
    } catch (e) {
      toast.error(e?.error || e?.message || "Bulk approval failed.");
    } finally {
      setBusy(false);
    }
  };

  const failed = result?.failed || [];

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={result ? "Bulk approval result" : `Approve ${ids.length} payment request(s)`}
      size="md"
      footer={result ? (
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      ) : (
        <>
          <Button variant="secondary" size="sm" disabled={busy} onClick={onHide}>Cancel</Button>
          <Button variant="success" size="sm" leftIcon={FaCheck} disabled={busy} onClick={onSubmit}>
            {busy ? "Approving..." : `Approve ${ids.length}`}
          </Button>
        </>
      )}
    >
      <div className="geist-scope">
        {result ? (
          <>
            <p className="tw-text-sm tw-text-geist-fg-2 tw-mb-3">
              Approved <strong>{(result.approved || []).length}</strong> of {ids.length}.
              {failed.length > 0 && " The following could not be signed:"}
            </p>
            {failed.length > 0 && (
              <div className="tw-max-h-64 tw-overflow-y-auto tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-divide-y tw-divide-geist-border">
                {failed.map((f) => (
                  <div key={f.id} className="tw-px-3 tw-py-2">
                    <div className="tw-text-[12px] tw-font-semibold tw-text-geist-fg">{f.code || `#${f.id}`}</div>
                    <div className="tw-text-[11px] tw-text-geist-error tw-mt-0.5">{f.error}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="tw-text-sm tw-text-geist-fg-2 tw-mb-3">
              Your signature (from HR Hub) will be attached to each of the {ids.length} selected
              requests. Requests that are no longer awaiting you will be skipped and reported.
            </p>
            <FormInput
              label="Comment (optional — applied to all)"
              type="textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

BulkApproveForm.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onDone: PropTypes.func,
};

export default BulkApproveForm;
